import { useCallback, useEffect, useState } from "react";
import { Download, Loader2, Mail } from "lucide-react";
import api from "../lib/apiClient";
import { Button } from "./ui/button";

export const EmailConsentPanel = ({ refreshing }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [downloadError, setDownloadError] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try { setData((await api.get("/admin/researcher-consents")).data); }
    catch { setError("Couldn't load consent records. Please try again."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (!refreshing) load(); }, [refreshing, load]);

  const download = async () => {
    setDownloading(true);
    setDownloadError("");
    try {
      const response = await api.get("/admin/researcher-consents/export", { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `ethos-researcher-consents-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { setDownloadError("Couldn't download the CSV. Please try again."); }
    finally { setDownloading(false); }
  };

  return (
    <section className="card-soft rounded-2xl overflow-hidden mb-8" data-testid="email-consent-panel" aria-labelledby="email-consent-heading">
      <div className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-5 border-b border-border">
        <div>
          <span className="eyebrow flex items-center gap-2"><Mail size={14} aria-hidden="true" /> Researcher contacts</span>
          <h2 id="email-consent-heading" className="font-display font-bold text-lg mt-2" data-testid="email-consent-heading">Email consent records</h2>
          <p className="text-sm text-muted-foreground mt-1" data-testid="email-consent-count">
            {loading ? "Loading records…" : error ? "Records unavailable" : `${data.total} saved consent ${data.total === 1 ? "record" : "records"}`}
          </p>
        </div>
        <Button onClick={download} disabled={loading || downloading || !!error} data-testid="download-email-csv-button" className="rounded-xl h-11 px-5 shrink-0">
          {downloading ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Download aria-hidden="true" />}
          {downloading ? "Preparing CSV…" : "Download CSV"}
        </Button>
      </div>
      <p className="px-6 py-4 text-sm text-muted-foreground bg-[#f9f6ef]" data-testid="email-consent-notice">
        Saved from the verification screen. The CSV includes email, consent wording, confirmations, and UTC timestamps. No emails are sent; addresses are self-reported, not verified.
      </p>
      {downloadError && <p role="alert" className="px-6 py-3 text-sm text-destructive" data-testid="email-csv-error">{downloadError}</p>}
      {error ? <div className="p-6" role="alert" data-testid="email-consent-error"><p className="text-sm text-destructive">{error}</p><Button variant="outline" onClick={load} className="mt-3" data-testid="email-consent-retry">Try again</Button></div> :
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="email-consent-table">
            <caption className="sr-only">Ten most recently confirmed email addresses. Download CSV for all consent records.</caption>
            <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th scope="col" className="px-6 py-3">Email</th><th scope="col" className="px-6 py-3">Consent</th><th scope="col" className="px-6 py-3">Last confirmed (UTC)</th><th scope="col" className="px-6 py-3">Source</th>
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={4} className="px-6 py-8 text-muted-foreground" data-testid="email-consent-loading">Loading consent records…</td></tr> : !data.recent.length ?
                <tr><td colSpan={4} className="px-6 py-10 text-center text-muted-foreground" data-testid="email-consent-empty">No emails captured yet. New verification submissions will appear here.</td></tr> :
                data.recent.map((record) => (
                  <tr key={record.id} className="border-b border-border last:border-0" data-testid={`email-consent-row-${record.id}`}>
                    <td className="px-6 py-4 font-medium break-all">{record.email}</td>
                    <td className="px-6 py-4"><span className="inline-block rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 px-2.5 py-1 text-xs whitespace-nowrap">Consent recorded</span></td>
                    <td className="px-6 py-4 text-muted-foreground whitespace-nowrap"><time dateTime={record.last_consented_at}>{new Date(record.last_consented_at).toISOString().replace("T", " ").slice(0, 19)}</time></td>
                    <td className="px-6 py-4 text-muted-foreground whitespace-nowrap">Researcher verification</td>
                  </tr>
                ))}
            </tbody>
          </table>
          {!loading && data.total > 10 && <p className="px-6 py-3 text-xs text-muted-foreground" data-testid="email-consent-page-note">Showing the 10 most recent records. Download CSV for the complete list.</p>}
        </div>
      }
    </section>
  );
};
